/**
 * Cover / variant display sorting for PDP default swatch row, View All groups,
 * and PLP tile swatches (DE-780). Consumes `c_popularCovers` from DE-761.
 */

/** Default PDP row size before "+N More" (see CoverSwatchGrid). */
export const DEFAULT_COVER_SWATCH_ROW_LIMIT = 7;

/**
 * Canonical light → dark colour-group rank (DE-780 AC4 / AC5).
 * Unknown / missing labels map to Other (last).
 */
export const COLOUR_GROUP_RANK = Object.freeze({
  white: 1,
  cream: 2,
  grey: 3,
  gray: 3,
  yellow: 4,
  orange: 5,
  red: 6,
  pink: 7,
  green: 8,
  blue: 9,
  purple: 10,
  brown: 11,
  black: 12,
  other: 13,
});

const OTHER_COLOUR_RANK = COLOUR_GROUP_RANK.other;

/**
 * @param {unknown} raw
 * @returns {string[]}
 */
export function parsePopularCoverIds(raw) {
  if (raw == null || raw === '') {
    return [];
  }

  if (Array.isArray(raw)) {
    return raw.map((id) => String(id).trim()).filter(Boolean);
  }

  if (typeof raw === 'string') {
    const trimmed = raw.trim();

    if (!trimmed) {
      return [];
    }

    if (trimmed.startsWith('[')) {
      try {
        const parsed = JSON.parse(trimmed);

        if (Array.isArray(parsed)) {
          return parsed.map((id) => String(id).trim()).filter(Boolean);
        }
      } catch {
        // fall through to delimited parse
      }
    }

    return trimmed
      .split(/[,|]/)
      .map((id) => id.trim())
      .filter(Boolean);
  }

  return [];
}

/**
 * Read ordered popular cover variant product IDs from a master product.
 *
 * @param {object|null|undefined} product
 * @returns {string[]}
 */
export function getPopularCoverIds(product) {
  return parsePopularCoverIds(product?.c_popularCovers);
}

/**
 * Map refinementColour / colour-group label → rank (1–13).
 * Multi-token labels (e.g. "Black / Dark Grey") use the first matching token.
 *
 * @param {string|null|undefined} label
 * @returns {number}
 */
export function getColourGroupRank(label) {
  if (label == null || typeof label !== 'string') {
    return OTHER_COLOUR_RANK;
  }

  const normalized = label.trim().toLowerCase();

  if (!normalized) {
    return OTHER_COLOUR_RANK;
  }

  if (Object.prototype.hasOwnProperty.call(COLOUR_GROUP_RANK, normalized)) {
    return COLOUR_GROUP_RANK[normalized];
  }

  const tokens = normalized
    .split(/[^a-z0-9]+/)
    .map((t) => t.trim())
    .filter(Boolean);

  for (const token of tokens) {
    if (Object.prototype.hasOwnProperty.call(COLOUR_GROUP_RANK, token)) {
      return COLOUR_GROUP_RANK[token];
    }
  }

  return OTHER_COLOUR_RANK;
}

/**
 * @param {object|null|undefined} variant
 * @returns {number}
 */
export function getVariantSortPrice(variant) {
  const candidates = [
    variant?.salePrice,
    variant?.price,
    variant?.c_processedData?.salePrice,
    variant?.c_processedData?.price
  ];

  for (const value of candidates) {
    // `buildAllVariantsData` emits null for a missing price, and Number(null) is 0 —
    // skip empties so the next candidate (or Infinity) is used instead of a fake 0.
    if (value == null || value === '') {
      continue;
    }

    const n = typeof value === 'number' ? value : Number(value);

    if (Number.isFinite(n)) {
      return n;
    }
  }

  return Number.POSITIVE_INFINITY;
}

/**
 * @param {object|null|undefined} variant
 * @returns {string}
 */
function getVariantColourLabel(variant) {
  return (
    variant?.c_processedData?.refinementColor ??
    variant?.refinementColor ??
    variant?.c_refinementColor ??
    ''
  );
}

/**
 * @param {object|null|undefined} variant
 * @returns {string}
 */
export function getVariantProductId(variant) {
  const id = variant?.productId ?? variant?.id;

  return id != null ? String(id) : '';
}

/**
 * Default sort: price ascending, then colour light→dark, then productId.
 *
 * @param {object} a
 * @param {object} b
 * @returns {number}
 */
export function compareVariantsByPriceThenColour(a, b) {
  // Compare, not subtract: priceless rows are Infinity on both sides and
  // `Infinity - Infinity` is NaN, which sort() reads as "equal".
  const priceA = getVariantSortPrice(a);
  const priceB = getVariantSortPrice(b);

  if (priceA !== priceB) {
    return priceA < priceB ? -1 : 1;
  }

  const colourDiff =
    getColourGroupRank(getVariantColourLabel(a)) -
    getColourGroupRank(getVariantColourLabel(b));

  if (colourDiff !== 0) {
    return colourDiff;
  }

  return getVariantProductId(a).localeCompare(getVariantProductId(b));
}

/**
 * @param {Array} variants
 * @returns {Array}
 */
export function sortVariantsByPriceThenColour(variants) {
  if (!Array.isArray(variants) || variants.length < 2) {
    return Array.isArray(variants) ? [...variants] : [];
  }

  return [...variants].sort(compareVariantsByPriceThenColour);
}

/**
 * Popular covers first (attribute order), then remaining by default sort.
 * Used for PDP default row + full overflow list, and PLP cover order.
 *
 * @param {Array} variants
 * @param {string[]|null|undefined} [popularIds]
 * @returns {Array}
 */
export function sortCoverVariantsForDisplay(variants, popularIds) {
  if (!Array.isArray(variants) || variants.length === 0) {
    return [];
  }

  const byId = new Map();

  for (const variant of variants) {
    const id = getVariantProductId(variant);

    if (id && !byId.has(id)) {
      byId.set(id, variant);
    }
  }

  const popular = [];
  const used = new Set();
  const ids = Array.isArray(popularIds) ? popularIds : [];

  for (const rawId of ids) {
    const id = String(rawId).trim();

    if (!id || used.has(id)) {
      continue;
    }

    const match = byId.get(id);

    if (!match) {
      continue;
    }

    popular.push(match);
    used.add(id);
  }

  const rest = [];

  for (const variant of variants) {
    const id = getVariantProductId(variant);

    if (!id || used.has(id)) {
      continue;
    }

    rest.push(variant);
    used.add(id);
  }

  return [...popular, ...sortVariantsByPriceThenColour(rest)];
}

/**
 * Default swatch row: popular first (attribute order), top up to `limit` via default sort.
 *
 * @param {Array} variants
 * @param {string[]|null|undefined} popularIds
 * @param {{limit?: number}} [options]
 * @returns {Array}
 */
export function buildDefaultSwatchRow(variants, popularIds, {limit = DEFAULT_COVER_SWATCH_ROW_LIMIT} = {}) {
  const ordered = sortCoverVariantsForDisplay(variants, popularIds);

  if (!Number.isFinite(limit) || limit < 0) {
    return ordered;
  }

  return ordered.slice(0, limit);
}

/**
 * Sort Shop-by-Colour group rows light → dark; variants inside by price → colour.
 *
 * @param {Array<{color?: string, variants?: Array}>} groups
 * @returns {Array}
 */
export function sortColourGroups(groups) {
  if (!Array.isArray(groups)) {
    return [];
  }

  return [...groups]
    .map((row) => ({
      ...row,
      variants: sortVariantsByPriceThenColour(row.variants ?? []),
    }))
    .sort(
      (a, b) => getColourGroupRank(a.color) - getColourGroupRank(b.color) ||
        String(a.color ?? '').localeCompare(String(b.color ?? ''))
    );
}

/**
 * Sort Shop-by-Collection groups by cheapest variant price; variants inside by price → colour.
 *
 * @param {Array<{collectionName?: string, variants?: Array}>} groups
 * @returns {Array}
 */
export function sortCollectionGroups(groups) {
  if (!Array.isArray(groups)) {
    return [];
  }

  return [...groups]
    .map((row) => {
      const variants = sortVariantsByPriceThenColour(row.variants ?? []);

      return {...row, variants};
    })
    .sort((a, b) => {
      const minA = Math.min(
        ...((a.variants?.length ? a.variants : [{price: Number.POSITIVE_INFINITY}]).map(
          getVariantSortPrice
        ))
      );
      const minB = Math.min(
        ...((b.variants?.length ? b.variants : [{price: Number.POSITIVE_INFINITY}]).map(
          getVariantSortPrice
        ))
      );

      if (minA !== minB) {
        return minA - minB;
      }

      return String(a.collectionName ?? '').localeCompare(String(b.collectionName ?? ''));
    });
}

/**
 * Ordered unique cover variation values derived from sorted variants (for PLP chips).
 *
 * @param {Array} orderedVariants - output of {@link sortCoverVariantsForDisplay}
 * @param {Array<{value?: string}>} coverAttrValues - catalog cover attribute values
 * @returns {Array<{value?: string}>}
 */
export function orderCoverAttributeValues(orderedVariants, coverAttrValues) {
  if (!Array.isArray(coverAttrValues) || coverAttrValues.length === 0) {
    return [];
  }

  const byValue = new Map(
    coverAttrValues
      .filter((entry) => entry?.value != null && entry.value !== '')
      .map((entry) => [String(entry.value), entry])
  );

  const out = [];
  const used = new Set();

  for (const variant of orderedVariants ?? []) {
    const cover =
      variant?.variationValues?.cover ??
      variant?.c_processedData?.coverValue ??
      null;

    if (cover == null || cover === '') {
      continue;
    }

    const key = String(cover);

    if (used.has(key)) {
      continue;
    }

    const attr = byValue.get(key);

    if (!attr) {
      continue;
    }

    out.push(attr);
    used.add(key);
  }

  for (const entry of coverAttrValues) {
    const key = entry?.value != null ? String(entry.value) : '';

    if (!key || used.has(key)) {
      continue;
    }

    out.push(entry);
    used.add(key);
  }

  return out;
}
